(ns app.front.view.inspections.quick-pass
  (:require [app.domain.group.model :as group-model]
            [app.domain.inspection.model :as inspection-model]
            [app.front.entities :as entities]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.select :as ui-select]
            [app.front.ui.visibility-title :as ui-visibility]
            [app.front.view.inspections.booking-dialog :as booking-dialog]
            [app.front.view.inspections.recurrence :as recurrence]
            [app.front.view.inspections.time-tracking :as time-tracking]
            [app.front.view.workspace.get :as ws-get]
            [clojure.set :as set]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))


(rf/reg-sub ::modal-data
  :-> ::modal)


(rf/reg-sub ::inspection-templates
  (fn [[_ auth-group-id]]
    (rf/subscribe [::entities/entities {:entity/type            :entity.type/inspection-pdf-template
                                        :domain/authority-group auth-group-id}]))
  (fn [templates _]
    (->> templates
         (inspection-model/filter-inspection-templates-by-type #{:inspection.pdf-template.type/general-report}))))


(rf/reg-sub ::auth-group-has-multiple-templates?
  (fn [[_ auth-group-id]]
    (rf/subscribe [::inspection-templates auth-group-id]))
  (fn [templates _]
    (->> (or templates [])
         count
         (< 1))))


(rf/reg-sub ::valid-inputs?
  :<- [::modal-data]
  :<- [::booking-dialog/booking-date-time-slot]
  (fn [[{:keys [recurrence? inspection time-spent inspection-template]}
        {:keys [date time-slot-id]}] _]
    (and (or (-> inspection
                 inspection-model/authority-group
                 :group/features
                 set
                 :group.feature/inspection-time-tracking
                 not)
             (number? time-spent))
         (or inspection-template
             (not (<sub [::auth-group-has-multiple-templates?
                         (-> inspection inspection-model/authority-group :db/id)])))
         (or (not recurrence?)
             (and date time-slot-id)))))


(rf/reg-event-fx ::open-modal
  (fn [{:keys [db]} [_ inspection]]
    {:db       (assoc db ::modal {:inspection inspection})
     :dispatch [::ws-get/get-workspace-config (-> inspection inspection-model/authority-group :group/id)]}))


(rf/reg-event-db ::close-modal
  #(dissoc % ::modal))


(defn get-quick-pass-parameters [db]
  (let [{:keys [inspection recurrence?] :as modal-data} (::modal db)
        booking-data                                    (::booking-dialog/booking db)]
    (-> (update modal-data :inspection :db/id)
        (util/safe-update-in [:inspection-template] :db/id)
        (set/rename-keys {:inspection :inspection-id :inspection-template :inspection-template-id})
        (dissoc :recurrence?)
        (merge (when recurrence?
                 {:recurrence (recurrence/get-recurrence-params inspection booking-data)})))))


(rf/reg-event-fx ::quick-pass-inspection
  (fn [{:keys [db]} _]
    (let [{:keys [inspection]} (::modal db)]
      {:comm/command {:command    :process/apply-signal
                      :data       {:process/signal [:process.signal/quick-pass-inspection
                                                    (get-quick-pass-parameters db)]
                                   :entity-id      (-> inspection :entity/parent :db/id)}
                      :on-success [::perform-quick-pass.success]}})))


(rf/reg-event-fx ::perform-quick-pass.success
  (fn [{:keys [db]} [_ status response]]
    {:db         (dissoc db ::modal)
     :dispatch-n [[::entities/apply-changes status response]
                  [:notifications/add :toast :success :success :changes-saved]]}))


(rf/reg-event-db ::update-value
  (fn [db [_ attr value]]
    (assoc-in db [::modal attr] value)))


(defn quick-pass-modal
  "The modal dialog for entering the minimum info required for inspection visit completion for quick pass
  Note that quick pass cannot be used for occupancy inspections"
  [{:keys [tr]}]
  (let [{:keys [inspection notes time-spent inspection-template]
         :as   data}                                             (<sub [::modal-data])
        auth-group                                               (-> inspection inspection-model/authority-group)
        tr                                                       (group-model/group-tr-fn tr auth-group)
        has-feature?                                             (-> auth-group :group/features set)]
    (when (has-feature? :group.feature/inspection-quick-pass)
      [ui-modal/composed-input-modal
       {:id       "quick-pass-modal"
        :open     (some? data)
        :on-close #(>evt [::close-modal])} ;; Other modals can open this, so get on top of them.
       (cc/modal-header
         {:close-modal #(>evt [::close-modal])
          :title       (tr [:inspection/quick-pass])})
       (cc/modal-body
         {}
         [:div.flex.flex-col.space-y-12
          ;; Public notes for the visit
          [ui-rt/editor-legacy {:id                      "quick-pass-inspection-notes"
                                :label-text              [ui-visibility/visibility-title
                                                          {:visibility :public
                                                           :tr         tr}
                                                          (str (tr [:inspection.visit/notes]) " (" (tr [:optional]) ")")]
                                :initial-value           notes
                                :on-change               #(>evt [::update-value :notes %])
                                :default-phrase-category (-> auth-group :group/phrase-category-inspections)
                                :authority-group-id      (:db/id auth-group)
                                :placeholder             (tr [:inspection.visit/notes-placeholder])}]


          ;; Template that is used when generating inspection report
          ;; If the auth-group is only using one template then the one template will be used by default
          (when (<sub [::auth-group-has-multiple-templates? (:db/id auth-group)])
            [ui-select/select
             {:values       (<sub [::inspection-templates (:db/id auth-group)])
              :class-name   "full-width-select"
              :id           "quick-pass-pdf-template-select"
              :label-text   (tr [:inspection.visit/inspection-report-template-select])
              :val->str     :inspection.pdf-template/name
              :select-text  (tr [:inspection.visit/none])
              :on-change    #(>evt [::update-value :inspection-template %])
              :value        inspection-template
              :required     true
              :allow-empty? false}])
          ;; Time spent on visit
          (when (has-feature? :group.feature/inspection-time-tracking)
            [time-tracking/time-tracking
             {:tr        tr
              :value     time-spent
              :on-change #(>evt [::update-value :time-spent %])}])
          ;; Repeating the inspection in the future
          (when (has-feature? :group.feature/recurring-inspections)
            [recurrence/view
             {:tr          tr
              :inspection  inspection
              :in-modal?   true
              :values      {:create-inspection? (:recurrence? data)}
              :visit-state :inspection.visit.result/passed
              :on-change   #(>evt [::update-value :recurrence? %])}])])
       (cc/modal-footer
         {}
         (cc/button {:kind     :secondary
                     :on-click #(>evt [::close-modal])}
                    (tr [:cancel]))
         (cc/button {:kind     :primary
                     :disabled (not (<sub [::valid-inputs?]))
                     :on-click #(>evt [::quick-pass-inspection])}
                    (tr [:confirm])))])))


(defn quick-pass-button
  "Button that allows the inspector to create and mark as passed an inspection visit as a single operation"
  [{:keys [tr inspection]}]
  (cc/button
    {:size     :small
     :kind     :primary
     :on-click #(do (.stopPropagation %)
                    (>evt [::open-modal inspection]))}
    (tr [:inspection/quick-pass])))
